import { configureStore, Tuple, type StoreEnhancer } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/es/storage";
import { rootReducer } from "@store/rootReducer";
import createSagaMiddleware from "redux-saga";
import rootSaga from "@store/rootSaga";
import { createLogger } from "redux-logger";
import { isProd } from "@utils/CommonUtil";
import { setTestId, setTestStatus, setIsGazeOffBounds } from "@store/tests/testSlice";
import { selectPatient, clearPatient } from "@store/patients/patientSlice";
import {
  setNewTestExtension,
  setShowOrgDetailsInResults,
  navigateToNewTestPage,
} from "@store/ui/uiSlice";
import {
  requestDistributorSales,
  setOrgTestCounts,
  requestAllDistributorIds,
} from "@store/analytics/analyticsSlice";
import {
  fetchTestListRequest,
  clearTestList,
  setTestListFilters,
} from "@store/testList/testListSlice";
import {
  attemptWebSocketConnect,
  setWebSocketState,
  resetAllConnections,
} from "@store/connection/connectionSlice";
import { addCompletedTestId, resetTestSession } from "@store/testsession/testSessionSlice";
import { pruneStaleDeviceSelection } from "@utils/deviceStorage";

const isProduction = isProd();

let allowedSlices: Set<string> = new Set();
allowedSlices = new Set([]); // Add remove slices to your preference. Or simply comment this line to stop logging altogether.

const logger = isProduction
  ? null
  : createLogger({
      predicate: (_getState, action) => {
        const type = typeof action.type === "string" ? action.type : "";
        const slice = type.split("/")[0];
        return allowedSlices.has(slice);
      },
    });

const sagaMiddleware = createSagaMiddleware();

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    "user",
    "product",
    "extra",
    "test",
    "patient",
    "analytics",
    "ui",
    "userPreferences",
    "adminConfigurations",
  ], // Add the slices you want to persist
  blacklist: ["connection"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);
const sentryReduxEnhancer: StoreEnhancer | undefined =
  isProduction && process.env.REACT_APP_SENTRY_DSN
    ? Sentry.createReduxEnhancer({
        attachReduxState: false,
        actionTransformer: (action) => ({ type: action.type }),
      })
    : undefined;

const devToolsActionCreators = {
  // test actions
  setTestId,
  setTestStatus,
  setIsGazeOffBounds,
  // patient actions
  selectPatient,
  clearPatient,
  // ui actions
  setNewTestExtension,
  setShowOrgDetailsInResults,
  navigateToNewTestPage,
  // analytics actions
  requestDistributorSales,
  setOrgTestCounts,
  requestAllDistributorIds,
  // test list actions
  fetchTestListRequest,
  clearTestList,
  setTestListFilters,
  // connection actions
  attemptWebSocketConnect,
  setWebSocketState,
  resetAllConnections,
  // test session actions
  addCompletedTestId,
  resetTestSession,
};

export const store = configureStore({
  reducer: persistedReducer,
  enhancers: (getDefaultEnhancers) => {
    const defaultEnhancers = getDefaultEnhancers();
    return sentryReduxEnhancer ? defaultEnhancers.concat(sentryReduxEnhancer) : defaultEnhancers;
  },
  middleware: () => {
    const middleware = [sagaMiddleware];
    if (logger) {
      middleware.push(logger);
    }
    return new Tuple(...middleware);
  },
  devTools: isProduction
    ? false
    : {
        actionCreators: devToolsActionCreators,
      },
});

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store, null, () => pruneStaleDeviceSelection(store));

export type RootStateWithoutPersist = ReturnType<typeof rootReducer>;
export type RootState = RootStateWithoutPersist;
export type AppDispatch = typeof store.dispatch;
